import { DownOutlined } from '@ant-design/icons';
import type { SelectProps } from 'antd';
import { Button, Popover, Skeleton, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import Select from '@/components/Select';

import {
  ButtonRow,
  LabelContainer,
  LabelText,
  PopoverContainer,
  PopoverContent,
  TitleText
} from './styles';

export type Props = {
  isLoading?: boolean;
  title: string;
  options: SelectProps['options'];
  label?: string;
  currentValue?: string;
  onConfirm: (selectedValue: string) => void;
};

const DropdownPopover = ({
  isLoading,
  title,
  options,
  label,
  currentValue,
  onConfirm
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(currentValue);

  useEffect(() => {
    if (currentValue) {
      setSelectedValue(currentValue);
      return;
    }

    if (options?.length) {
      setSelectedValue(options[0].value as string);
    }
  }, [currentValue, options, isLoading]);

  const selectOptionLabel = useMemo(
    () => options?.find(option => option.value === currentValue)?.label,
    [options, currentValue]
  );

  const handleSelectChange = (value: string) => {
    setSelectedValue(value);
  };

  const handleConfirm = () => {
    onConfirm(selectedValue as string);
    setIsOpen(false);
  };

  const popoverContent = (
    <PopoverContent direction='vertical'>
      <Typography.Text>{label}</Typography.Text>
      <Select
        options={options}
        value={selectedValue}
        onChange={handleSelectChange}
        loading={isLoading}
        data-testid='select-dropdown'
      />
      <ButtonRow>
        <Button type='primary' loading={isLoading} onClick={handleConfirm}>
          Select
        </Button>
      </ButtonRow>
    </PopoverContent>
  );

  return (
    <Popover
      content={popoverContent}
      trigger='click'
      placement='bottomLeft'
      onOpenChange={setIsOpen}
      open={isOpen}>
      <PopoverContainer>
        <TitleText>{title}</TitleText>
        <LabelContainer>
          {isLoading ? (
            <Skeleton.Input size='small' data-testid='skeleton-loader' active />
          ) : (
            <>
              <LabelText strong>{selectOptionLabel ?? label}</LabelText>
              <DownOutlined />
            </>
          )}
        </LabelContainer>
      </PopoverContainer>
    </Popover>
  );
};

export default DropdownPopover;
